<script setup>
defineOptions({
  name: 'AuthPageLayout'
})
</script>

<template>
  <div class="apl-Layout">
    <div class="apl-Layout_Content">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.apl-Layout {
  margin-inline: auto;
  width: 100%;
  max-width: $page-width-lg;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  --clerk-spacing: 16px;

  --clerk-color-primary: #{$primary-color-next};
  --clerk-color-muted-foreground: #{$dark-1};
  --clerk-color-foreground: #{$dark-3};
  --clerk-color-danger: #{$grade-low-color-next};
  --clerk-color-success: #{$grade-high-color-next};
  --clerk-color-warning: #{$grade-medium-color-next};
  --clerk-font-size-md: #{$fs-14};
}

.apl-Layout_Content {
  background-color: $white;
}
</style>

<style lang="scss">
.apl-Layout {
  $controls-size: 40px;

  .cl-logoBox {
    margin-bottom: calc(var(--clerk-spacing) / 2);

    .cl-logoImage {
      width: 143px;
      height: 40px;
    }
  }

  .cl-headerTitle {
    --clerk-color-foreground: #{$dark-1};
  }

  %remove-outline-effects {
    outline: none;
    box-shadow: none;

    &:hover,
    &:active,
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  .cl-socialButtons {
    .cl-button {
      @extend %remove-outline-effects;
    }
  }

  .cl-socialButtonsBlockButton {
    --apl-social-button-bg: rgb(243, 245, 249);
    background: var(--apl-social-button-bg);
    border-color: var(--apl-social-button-bg);

    &:hover {
      --apl-social-button-bg: rgb(233, 236, 243);
    }

    &:active,
    &:focus {
      --apl-social-button-bg: rgb(221, 223, 229);
    }
  }

  .cl-input {
    border-color: $grey-2-next;
    max-height: $controls-size;
    height: $controls-size;

    &[data-variant='default'] {
      border-width: 2px;

      @extend %remove-outline-effects;

      &:focus {
        border-color: $primary-color-next;
      }
    }

    &[data-feedback='error'] {
      border-color: var(--clerk-color-danger);
    }

    &[data-feedback='success'] {
      border-color: var(--clerk-color-success);
    }
  }

  .cl-formFieldInputShowPasswordButton {
    @extend %remove-outline-effects;
  }

  .cl-button[data-variant='solid'][data-color='primary'] {
    min-height: $controls-size;
    @extend %remove-outline-effects;
    --accentHover: var(--primary-color-next-700);

    &:hover {
      border-color: var(--accentHover);
    }

    &:active {
      --accentHover: var(--primary-color-next-500);
    }

    &:after {
      display: none;
    }
  }

  .cl-buttonArrowIcon {
    display: none;
  }

  %lh-16 {
    line-height: 16px;
  }

  $form-gap: 20px;
  $form-field-gap: 6px;

  .cl-form {
    gap: $form-gap;

    .cl-formFieldLabel {
      font-size: $fs-12;
      @extend %lh-16;
    }

    .cl-formFieldErrorText,
    .cl-formFieldInfoText,
    .cl-formFieldSuccessText,
    .cl-formFieldWarningText {
      font-weight: fw('regular');
      font-size: $fs-12;
      @extend %lh-16;
    }
  }

  .cl-cardBox {
    border: 1px solid rgba($white, 0.3);
    box-shadow: $secondary-box-shadow;
    border-radius: 20px;
  }

  .cl-card {
    &:not(.cl-signIn-havingTrouble, .cl-signIn-password) {
      box-shadow: inset 0 -1px 0 $grey-2-next;
    }

    border-radius: 0;
    border: none;
    padding: calc(var(--clerk-spacing) * 2.5) calc(var(--clerk-spacing) * 2)
      calc(var(--clerk-spacing) * 2);
  }

  .cl-main {
    gap: var(--clerk-spacing);
  }

  @mixin cleanup-footer {
    background: none;
    box-shadow: none;
    border: none;
  }

  @mixin footer-bt {
    border-top: 1px solid $grey-2-next;
  }

  @mixin cleanup-bg {
    display: none;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
  }

  @mixin social-button-gap {
    gap: calc(var(--clerk-spacing) / 4);
  }

  .cl-footer {
    @include cleanup-footer;
  }

  @supports selector(:has(*)) {
    div:has(> div > div > .cl-footerPages) {
      @include cleanup-footer;
      @include footer-bt;
    }

    div:has(+ div > div > .cl-footerPages) {
      @include cleanup-bg;
    }

    .cl-socialButtons .cl-socialButtonsBlockButton {
      span:has(span.cl-socialButtonsBlockButtonText) {
        @include social-button-gap;
      }
    }

    .cl-form {
      div:has(> .cl-formFieldRow) {
        gap: $form-gap;
      }

      .cl-formField {
        > div:has(> .cl-formFieldLabelRow) {
          gap: $form-field-gap;
        }
      }
    }
  }

  @supports not selector(:has(*)) {
    .cl-internal-1dauvpw /* weird classname of footer privacy & terms links & notice of development mode */ {
      @include cleanup-footer;
      @include footer-bt;
    }

    .cl-internal-1k7jtru /* weird classname of system div with background for footer */ {
      @include cleanup-bg;
    }

    .cl-socialButtons .cl-socialButtonsBlockButton {
      .cl-internal-11tkpc3 /* weird classname of wrapper of icon & text in social button */ {
        gap: calc(var(--clerk-spacing) / 4);
      }
    }

    .cl-form {
      .cl-internal-ht35d7 /* weird classname of all form fields wrapper */ {
        gap: $form-gap;
      }

      .cl-internal-17uj465 /* weird classname of single form field wrapper */ {
        gap: $form-field-gap;
      }
    }
  }
}
</style>
